import { useMemo } from "react";
import { useTranslation } from "@/hooks/useTranslation";
import type { EventTemplateDefinition } from "@/lib/event-templates";
import { offeredTemplatesOf, packageNameOf, templateCardPreviewOf } from "@/lib/template-engine/editorTemplate";
import { ChoiceCard } from "../ChoiceCard";

interface DesignPickerProps {
  /** `GET /templates`; null while it has not answered or after it failed, when the built-in designs are offered. */
  catalog: EventTemplateDefinition[] | null;
  /** The event's current design. */
  value: string | null;
  disabled?: boolean;
  /** Id of the element that names the choice. */
  labelledBy?: string;
  onSelect: (key: string) => void;
}

/**
 * JUB-213: the designs a couple can switch to on the reward screen (W10): the
 * built-in designs and every active installed template package, from the same
 * catalog and the same rule (`isOfferedTemplate`) as the editor's Landing tab.
 * A package shows its manifest name and description, a built-in design its
 * translated catalog name and tagline.
 */
export function DesignPicker({ catalog, value, disabled, labelledBy, onSelect }: DesignPickerProps) {
  const { t } = useTranslation();
  const options = useMemo(() => offeredTemplatesOf(catalog), [catalog]);

  return (
    <div className="space-y-3" role="radiogroup" aria-labelledby={labelledBy} data-testid="onboarding-design-options">
      {options.map((template) => {
        const preview = templateCardPreviewOf(template);
        const packageName = packageNameOf(template);
        const title =
          packageName ??
          t(`events.form.templateCatalog.${template.key}.name`, { defaultValue: template.name });
        const description =
          packageName !== null
            ? preview.tagline
            : t(`events.form.templateCatalog.${template.key}.tagline`, { defaultValue: preview.tagline });
        const selected = template.key === value;
        return (
          <ChoiceCard
            key={template.key}
            selected={selected}
            onSelect={() => {
              if (!selected) onSelect(template.key);
            }}
            title={title}
            description={description || undefined}
            disabled={disabled}
            testId={`onboarding-design-option-${template.key}`}
            icon={
              <span
                className="flex h-12 w-12 items-center justify-center rounded-xl border text-2xl"
                style={{ background: preview.background }}
                aria-hidden="true"
              >
                {preview.emoji}
              </span>
            }
          />
        );
      })}
    </div>
  );
}
